/**
 * Auth Events Dashboard
 *
 * Shows login history, failures, and auth health statistics.
 */

import { useState } from 'react'
import {
  useAuthEvents,
  useAuthEventsStats,
  EVENT_TYPE_CONFIG,
  type AuthEvent,
} from '../hooks/useAuthEvents'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Shield,
  CheckCircle2,
  XCircle,
  Users,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

const PAGE_SIZE = 50

function StatCard({
  icon: Icon,
  label,
  value,
  valueClassName,
}: {
  icon: React.ElementType
  label: string
  value: string | number
  valueClassName?: string
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          <div className="rounded-lg bg-muted p-2">
            <Icon className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={`text-2xl font-bold ${valueClassName || ''}`}>{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function EventTypeBadge({ type }: { type: string }) {
  const config = EVENT_TYPE_CONFIG[type] || { label: type, variant: 'outline' as const }
  return <Badge variant={config.variant}>{config.label}</Badge>
}

function truncateUA(ua: string | null): string {
  if (!ua) return '-'
  // Extract browser name from user agent
  const match = ua.match(/(Chrome|Firefox|Safari|Edge|Opera|curl|Python)[\s/][\d.]+/)
  return match ? match[0] : ua.slice(0, 40) + (ua.length > 40 ? '...' : '')
}

function EventRow({ event }: { event: AuthEvent }) {
  const metadata = event.metadata as Record<string, unknown> | null
  const isFailure = event.event_type !== 'login_success'

  return (
    <TableRow className={isFailure ? 'bg-destructive/5' : undefined}>
      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
        <span title={event.created_at ? new Date(event.created_at).toISOString() : ''}>
          {event.created_at
            ? formatDistanceToNow(new Date(event.created_at), { addSuffix: true })
            : '-'}
        </span>
      </TableCell>
      <TableCell>
        <EventTypeBadge type={event.event_type} />
      </TableCell>
      <TableCell className="font-mono text-sm">
        {event.email || '-'}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {event.ip_address || '-'}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground" title={event.user_agent || ''}>
        {truncateUA(event.user_agent)}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
        {metadata?.['error'] ? String(metadata['error']).slice(0, 60) : metadata?.['path'] ? String(metadata['path']) : '-'}
      </TableCell>
    </TableRow>
  )
}

function StatsCards() {
  const { data: stats, isLoading } = useAuthEventsStats()

  if (isLoading) {
    return (
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <Skeleton className="h-16 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (!stats) return null

  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        icon={CheckCircle2}
        label="Logins (24h)"
        value={stats.loginsToday}
        valueClassName="text-green-600 dark:text-green-400"
      />
      <StatCard
        icon={stats.failuresToday > 0 ? AlertTriangle : XCircle}
        label="Failures (24h)"
        value={stats.failuresToday}
        valueClassName={stats.failuresToday > 0 ? 'text-red-600 dark:text-red-400' : ''}
      />
      <StatCard
        icon={Users}
        label="Unique Users (7d)"
        value={stats.uniqueUsersWeek}
      />
      <StatCard
        icon={Shield}
        label="Last Login"
        value={stats.lastLogin?.created_at
          ? formatDistanceToNow(new Date(stats.lastLogin.created_at), { addSuffix: true })
          : 'Never'}
      />
    </div>
  )
}

export function AuthEventsContent() {
  const [page, setPage] = useState(0)
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [emailSearch, setEmailSearch] = useState('')

  const { data, isLoading } = useAuthEvents({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    type: typeFilter || undefined,
    email: emailSearch || undefined,
  })

  const events: AuthEvent[] = data?.events ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="space-y-6">
      {/* Stats */}
      <StatsCards />

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Event Log</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <Input
              placeholder="Search by email..."
              value={emailSearch}
              onChange={(e) => {
                setEmailSearch(e.target.value)
                setPage(0)
              }}
              className="max-w-xs"
            />
            <Select
              value={typeFilter || '__all__'}
              onValueChange={(v) => {
                setTypeFilter(v === '__all__' ? '' : v)
                setPage(0)
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All event types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All event types</SelectItem>
                {Object.entries(EVENT_TYPE_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-sm text-muted-foreground self-center ml-auto">
              {total} event{total !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No auth events recorded yet</p>
              <p className="text-sm mt-1">Events will appear here after the next login attempt</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[140px]">When</TableHead>
                      <TableHead className="w-[140px]">Event</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="w-[120px]">IP</TableHead>
                      <TableHead className="w-[160px]">Browser</TableHead>
                      <TableHead className="w-[200px]">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((event) => (
                      <EventRow key={event.id} event={event} />
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page + 1} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
